import {useViewportId} from "@AppBuilderLib/entities/viewport/model/useViewportId";
import type {ToolbarCommandItem} from "@AppBuilderLib/features/appbuilder/config/toolbarRenderTypes";
import {useShapeDiverStoreStandardContainers} from "@AppBuilderLib/features/appbuilder/model/useShapeDiverStoreStandardContainers";
import {useShapeDiverStoreToolbars} from "@AppBuilderLib/features/appbuilder/model/useShapeDiverStoreToolbars";
import {useNotificationStore} from "@AppBuilderLib/features/notifications/model/useNotificationStore";
import {QUERYPARAM_AGENTURL} from "@AppBuilderLib/shared/config/queryparams";
import {getEnvironmentIdentifier} from "@AppBuilderLib/shared/lib/platform/environment";
import {useProps} from "@mantine/core";
import {useCallback, useEffect, useRef, useState} from "react";
import type {
	AppBuilderAgentOverlayProps,
	UseAppBuilderAgentHostProps,
} from "../config/appBuilderAgentHost";
import {
	hasPlacedHostedAgentWidget,
	warnDuplicateHostedAgentWidgets,
} from "../lib/hasPlacedHostedAgentWidget";
import {openAgentWindow} from "../lib/openAgentWindow";
import {resolveAgentUi} from "../lib/resolveAgentUi";
import {resolveAgentUrl} from "../lib/resolveAgentUrl";
import {useAgentToolTransports} from "./useAgentToolTransports";
import {useHostedAgentFrameStore} from "./useHostedAgentFrameStore";

/**
 * Hosts AppBuilderAgent on an App Builder page: toolbar button, iframe or
 * window, and ToolsApi. Not the LangChain agent itself.
 */
export function useAppBuilderAgentHost(
	props: UseAppBuilderAgentHostProps,
): AppBuilderAgentOverlayProps {
	const {namespace, appBuilderData, appBuilderParseSettled, sessionInfo} =
		props;
	const {viewportId} = useViewportId();
	const themeProps = useProps("AgentUi", {mode: "iframe" as const}, {});
	const {mode, showThreadHistory, createThreadOnLoad} =
		resolveAgentUi(themeProps);

	const resolvedAgentUrl = resolveAgentUrl(
		new URLSearchParams(window.location.search).get(QUERYPARAM_AGENTURL),
		getEnvironmentIdentifier(),
	);
	const iframeWindow = useHostedAgentFrameStore((state) => state.frame);
	const [popupWindow, setPopupWindow] = useState<Window | null>(null);
	// A registered hostedAgent iframe is the peer even in window mode.
	// Placed widgets hide the toolbar, so no popup is opened for that embed.
	const agentWindow =
		mode === "window" ? (iframeWindow ?? popupWindow) : iframeWindow;
	const placedHostedAgent = hasPlacedHostedAgentWidget(appBuilderData);
	const [panelMounted, setPanelMounted] = useState(false);
	const [panelVisible, setPanelVisible] = useState(false);

	const {snapshotComplete, agentConfig, peerConnected} =
		useAgentToolTransports({
			namespace,
			appBuilderData,
			appBuilderParseSettled,
			agentWindow,
			sessionInfo,
			showThreadHistory,
			createThreadOnLoad,
		});
	const agentWindowRef = useRef(agentWindow);
	agentWindowRef.current = agentWindow;
	const peerConnectedRef = useRef(peerConnected);
	peerConnectedRef.current = peerConnected;

	const agentUrl =
		snapshotComplete && agentConfig ? resolvedAgentUrl : undefined;
	const agentUrlRef = useRef(agentUrl);
	agentUrlRef.current = agentUrl;
	const name = agentConfig?.name?.trim();
	const label = name ? name : "Agent";

	const openWindow = useCallback(() => {
		if (!agentUrl) {
			return;
		}
		const existing = agentWindowRef.current;
		if (existing && !existing.closed && peerConnectedRef.current) {
			try {
				existing.focus();
				return;
			} catch {
				// The window is still open but cannot be focused. Open it again.
			}
		}
		const opened = openAgentWindow(agentUrl);
		if (!opened) {
			useNotificationStore.getState().show({
				title: "Could not open agent window.",
				message:
					"The agent window is not connected. Close it if it is open, then try Open agent again.",
				color: "red",
			});
			return;
		}
		setPopupWindow(null);
		window.setTimeout(() => setPopupWindow(opened), 0);
	}, [agentUrl]);

	const panelMountedRef = useRef(false);
	const panelVisibleRef = useRef(false);
	const modeRef = useRef(mode);
	modeRef.current = mode;
	const openWindowRef = useRef(openWindow);
	openWindowRef.current = openWindow;

	const onToggleAgent = useCallback(() => {
		if (!agentUrlRef.current) {
			return;
		}
		if (modeRef.current === "window") {
			openWindowRef.current();
			return;
		}
		if (!panelMountedRef.current) {
			panelMountedRef.current = true;
			panelVisibleRef.current = true;
			setPanelMounted(true);
			setPanelVisible(true);
			return;
		}
		const nextVisible = !panelVisibleRef.current;
		panelVisibleRef.current = nextVisible;
		setPanelVisible(nextVisible);
	}, []);

	const activeTabIndices = useShapeDiverStoreStandardContainers(
		(state) => state.activeTabIndices,
	);
	const containerOpen = useShapeDiverStoreStandardContainers(
		(state) => state.containerOpen,
	);

	const appliedModeRef = useRef(mode);
	useEffect(() => {
		warnDuplicateHostedAgentWidgets(appBuilderData, {
			activeTabIndices,
			containerOpen,
		});
	}, [activeTabIndices, appBuilderData, containerOpen]);

	useEffect(() => {
		if (appliedModeRef.current === mode) {
			return;
		}
		appliedModeRef.current = mode;
		panelMountedRef.current = false;
		panelVisibleRef.current = false;
		setPanelMounted(false);
		setPanelVisible(false);
		setPopupWindow(null);
	}, [mode]);

	useEffect(() => {
		const id = viewportId ? `agentUi-${viewportId}` : "agentUi";
		const {setDefaultToolbar, removeDefaultToolbar} =
			useShapeDiverStoreToolbars.getState();
		if (placedHostedAgent || (snapshotComplete && !agentConfig)) {
			removeDefaultToolbar(id);
			return;
		}
		const command: ToolbarCommandItem = {
			id: "open-agent",
			type: "command",
			label,
			tooltip: label,
			icon: "tabler:message-chatbot",
			disabled: !agentUrl,
			props: {
				allowDuringExecution: true,
				execute: onToggleAgent,
			},
		};
		setDefaultToolbar({
			id,
			source: "default",
			viewportId,
			side: "bottom",
			align: "end",
			order: 0,
			visibility: "always",
			ariaLabel: label,
			groups: [[command]],
		});
		return () => {
			removeDefaultToolbar(id);
		};
	}, [
		agentConfig,
		agentUrl,
		label,
		onToggleAgent,
		placedHostedAgent,
		snapshotComplete,
		viewportId,
	]);

	return {
		agentUrl,
		mode,
		panelMounted: placedHostedAgent ? false : panelMounted,
		panelVisible: placedHostedAgent ? false : panelVisible,
	};
}
